import { WorksheetLinking, WorksheetLinkingFormData } from './LinkPriorWorksheetsForm';
import { CSSProperties, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Worksheet } from '../../types/worksheet';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Text, SelectV2 } from '@cimplico/components';
import { isNullOrUndefined } from '../new-workpaper/utils';

type LinkPriorWorksheetRowProps = {
    worksheetLinking: WorksheetLinking;
    priorWorksheets: Worksheet[];
    idx: number;
    usedWorksheetLinkings: Set<string>;
    style?: CSSProperties;
};

function LinkPriorWorksheetRow({ priorWorksheets, idx, usedWorksheetLinkings, style }: LinkPriorWorksheetRowProps): JSX.Element | null {
    const { control } = useFormContext<WorksheetLinkingFormData>();
    const worksheetLinking = useWatch({ control, name: `worksheetLinkings.${idx}` }) as WorksheetLinking;
    const currentWorksheet = worksheetLinking.currentWorksheet;
    return (
        <div
            className={`grid grid-cols-[400px_100px_auto] gap-x-6 border-b last:border-0 items-center py-2 px-4 ${
                worksheetLinking?.priorWorksheet ? '' : 'bg-red-50'
            }`}
            style={style}
        >
            <Text className={`${worksheetLinking.priorWorksheet ? ' text-gray-600' : 'text-red-600'} text-sm justify-self-start`} lines={2}>
                {currentWorksheet.name}
            </Text>
            <div className="flex justify-center">
                <ArrowRightIcon className={`h-5 w-5 ${worksheetLinking?.priorWorksheet ? 'text-indigo-600' : 'text-red-600'}`} />
            </div>
            <div className="flex items-center gap-1 justify-between pr-6">
                <PriorWorksheetSelect
                    priorWorksheets={priorWorksheets}
                    worksheetLinking={worksheetLinking}
                    idx={idx}
                    usedWorksheetLinkings={usedWorksheetLinkings}
                />
                <div className="h-5 w-5">
                    {!worksheetLinking?.priorWorksheet && <ExclamationCircleIcon className="h-5 w-5 text-red-600" />}{' '}
                </div>
            </div>
        </div>
    );
}

function PriorWorksheetSelect({ priorWorksheets, worksheetLinking, idx, usedWorksheetLinkings }: LinkPriorWorksheetRowProps): JSX.Element {
    const context = useFormContext<WorksheetLinkingFormData>();
    const [search, setSearch] = useState('');
    const bestMatch = useMemo(
        () =>
            !worksheetLinking.priorWorksheet ? priorWorksheets.find((pw) => worksheetLinking.currentWorksheet.name === pw.name) : undefined,
        [priorWorksheets, worksheetLinking.currentWorksheet.name, worksheetLinking.priorWorksheet]
    );
    const defaultOptions = useMemo(() => {
        return priorWorksheets.map((pw) => {
            return {
                label: pw.name,
                value: pw.id,
                isDisabled: usedWorksheetLinkings.has(pw.id),
                disabledHint: 'This worksheet has already been linked',
            };
        });
    }, [priorWorksheets, usedWorksheetLinkings]);

    const options = useMemo(() => {
        if (isNullOrUndefined(bestMatch)) {
            return defaultOptions;
        }
        const matchingOption = defaultOptions.find((opt) => opt.value === bestMatch.id)!;
        return [matchingOption, ...defaultOptions.filter((opt) => opt.value !== bestMatch.id)];
    }, [bestMatch, defaultOptions]);

    return (
        <div className="w-full">
            <Controller
                control={context.control}
                name={`worksheetLinkings.${idx}`}
                render={({ field }): JSX.Element => {
                    const current = field.value as WorksheetLinking;
                    const selectedOption = current.priorWorksheet
                        ? [{ label: current.priorWorksheet.name, value: current.priorWorksheet.id }]
                        : [];

                    return (
                        <SelectV2
                            showMenuAsRelative
                            placeholder="Select a prior worksheet..."
                            className="w-full"
                            options={options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()))}
                            onSearch={setSearch}
                            value={selectedOption}
                            onChange={(v) => {
                                if (v.length) {
                                    const { value: id, label: name } = v[0];
                                    field.onChange({
                                        currentWorksheet: current.currentWorksheet,
                                        priorWorksheet: { id, name },
                                        index: idx,
                                    });
                                } else {
                                    field.onChange({
                                        currentWorksheet: current.currentWorksheet,
                                        priorWorksheet: undefined,
                                        index: idx,
                                    });
                                }
                            }}
                            size="small"
                        />
                    );
                }}
            />
        </div>
    );
}

export default LinkPriorWorksheetRow;
